import { useEffect, useState } from "react";

// The nav gates (supplierLive, supplierReferrer) live in localStorage and are read
// while the menu renders, so nothing re-rendered when they changed: a supplier taken
// live by an admin kept the onboarding-only sidebar until the page was reloaded,
// which is what a user sees as "it appears after a hard refresh".
//
// This is the smallest thing that fixes that: the setters bump a version, components
// subscribe through useSyncExternalStore, and the values keep living in localStorage
// so a reload still starts from the right state.
type Listener = () => void;

let version = 0;
const listeners = new Set<Listener>();

export const bumpNavFlags = (): void => {
  version += 1;
  listeners.forEach((l) => l());
};

export const subscribeNavFlags = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Snapshot must be a primitive that CHANGES on every write, or React bails out. */
export const navFlagsVersion = (): number => version;

/**
 * Subscribe a component to the nav gates.
 *
 * Returns the version only so the subscription is live; callers read the flags
 * themselves through `isSupplierLive()` / `getReferrerFlags()`, which stay the single
 * source of truth.
 *
 * useState + useEffect rather than useSyncExternalStore: the runtime is React 18, but
 * `@types/react` is pinned at 17.0.0 here, which does not declare that hook — the dev
 * server's type checker then fails the build and CRA covers the portal with an error
 * overlay. This does the same job on either version and needs no type it cannot see.
 */
export const useNavFlags = (): number => {
  const [version_, setVersion] = useState(navFlagsVersion);
  useEffect(() => subscribeNavFlags(() => setVersion(navFlagsVersion())), []);
  return version_;
};
